import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateLifeInsuranceTariffGroup,
  useUpdateLifeInsuranceTariffGroup,
} from "../../../../common/api/queries/lifeInsuranceTariff";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../common/constants";
import { CreateUpdateLifeInsuranceTariffGroup, LifeInsuranceTariffGroup } from "../../types";

interface Props {
  open: boolean;
  tariffGroup?: LifeInsuranceTariffGroup;
  onFormCancel: () => void;
}

export const LifeInsuranceTariffGroupForm = ({ open, tariffGroup, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateLifeInsuranceTariffGroup>();

  const createTariffGroup = useCreateLifeInsuranceTariffGroup();
  const updateTariffGroup = useUpdateLifeInsuranceTariffGroup();

  useEffect(() => {
    if (open && tariffGroup) {
      form.setFieldsValue(tariffGroup);
    }
  }, [open, tariffGroup, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (tariffGroup) {
          updateTariffGroup.mutate(
            { id: tariffGroup.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "lifeInsuranceTariff.attrs", error.violations),
            },
          );
        } else {
          createTariffGroup.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "lifeInsuranceTariff.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t(
        tariffGroup ? "lifeInsuranceTariff.titles.updateTariffGroup" : "lifeInsuranceTariff.titles.createTariffGroup",
      )}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createTariffGroup.isPending || updateTariffGroup.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={tariffGroup} />

      <Form form={form} layout="vertical" name="lifeInsuranceTariffGroupForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="name"
          label={t("lifeInsuranceTariff.attrs.name")}
          rules={[validations.notBlank, validations.size(1, 255)]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
